import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentAvailabilityStatus,
  AssessmentOption,
  FacultyClassAssessment,
  FacultyClassDetail,
  FacultyClassQuestionAnalysisItem,
  FacultyClassStudent,
  FacultyReadinessLevel,
} from '../types';
import { portalApi } from '../utils/api';
import { formatScheduleRange } from '../utils/assessmentSchedule';
import AssessmentEvidenceSummary from '../components/AssessmentEvidenceSummary';
import MathMarkdown from '../components/MathMarkdown';
import SkillPercentageProgress from '../components/SkillPercentageProgress';
import SkillBreakdownDialog from '../components/SkillBreakdownDialog';
import EditClassDialog, { type EditClassPayload } from '../components/EditClassDialog';
import EditAssessmentDatesDialog, { type AssessmentDatesPayload } from '../components/EditAssessmentDatesDialog';
import AddAssessmentDialog, { type AddAssessmentPayload } from '../components/AddAssessmentDialog';

function classTitle(item: FacultyClassDetail) {
  return item.class_name || `${item.course_code} ${item.section_code}`;
}

function formatDate(value?: string | null) {
  if (!value) return 'TBD';

  // Keep DB date values stable across timezones by using the YYYY-MM-DD portion as a local calendar date.
  const isoDateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (isoDateParts) {
    const year = Number(isoDateParts[1]);
    const monthIndex = Number(isoDateParts[2]) - 1;
    const day = Number(isoDateParts[3]);
    const localDate = new Date(year, monthIndex, day);
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleDateString();
    }
  }

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toLocaleDateString();
  }

  return 'TBD';
}

function formatCourseDateRange(startDate?: string | null, endDate?: string | null) {
  if (!startDate && !endDate) return 'Course Dates: TBD';
  return `Course Dates: ${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function buildPortalSearch(educatorUserId?: string, assessmentId?: string) {
  const params = new URLSearchParams();
  if (educatorUserId) params.set('educator_user_id', educatorUserId);
  if (assessmentId) params.set('assessment_id', assessmentId);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function availabilityLabel(status: AssessmentAvailabilityStatus) {
  if (status === 'coming_soon') return 'Coming Soon';
  if (status === 'closed') return 'Inactive';
  return 'Active';
}

function availabilityColor(status: AssessmentAvailabilityStatus): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success';
  if (status === 'coming_soon') return 'warning';
  return 'default';
}

function formatDuration(seconds?: number | null) {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return 'N/A';
  }

  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
}

export default function FacultyClassPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const educatorUserId = searchParams.get('educator_user_id') || undefined;
  const selectedAssessmentId = searchParams.get('assessment_id') || undefined;
  const portalQuerySuffix = buildPortalSearch(educatorUserId, selectedAssessmentId);
  const educatorOnlyQuerySuffix = buildPortalSearch(educatorUserId);

  const [sortMode, setSortMode] = useState<'az' | 'status' | 'readiness'>('az');
  const [isSortDescending, setIsSortDescending] = useState(false);
  const [questionSortMode, setQuestionSortMode] = useState<'question' | 'correct_rate' | 'average_time'>('question');
  const [isQuestionSortDescending, setIsQuestionSortDescending] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assessmentDatesDialogOpen, setAssessmentDatesDialogOpen] = useState(false);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [flaggedSkillsDialogOpen, setFlaggedSkillsDialogOpen] = useState(false);
  const [masteredSkillsDialogOpen, setMasteredSkillsDialogOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const { data: detail, isLoading, error } = useQuery<FacultyClassDetail>({
    queryKey: ['portal', 'faculty', 'class-detail', sectionId, educatorUserId],
    queryFn: () => portalApi.getFacultyClassDetail(sectionId!, educatorUserId),
    enabled: !!sectionId,
  });
  const { data: assessmentOptions = [] } = useQuery<AssessmentOption[]>({
    queryKey: ['portal', 'faculty', 'assessments'],
    queryFn: portalApi.getFacultyAssessments,
  });
  const {
    data: students = [],
    isLoading: studentsLoading,
    error: studentsError,
  } = useQuery<FacultyClassStudent[]>({
    queryKey: ['portal', 'faculty', 'class-students', sectionId, educatorUserId, selectedAssessmentId],
    queryFn: () => portalApi.getFacultyClassStudents(sectionId!, educatorUserId, selectedAssessmentId),
    enabled: !!sectionId,
  });
  const {
    data: questionAnalysis = [],
    isLoading: questionAnalysisLoading,
    error: questionAnalysisError,
  } = useQuery<FacultyClassQuestionAnalysisItem[]>({
    queryKey: ['portal', 'faculty', 'class-question-analysis', sectionId, educatorUserId, selectedAssessmentId],
    queryFn: () => portalApi.getFacultyClassQuestionAnalysis(sectionId!, selectedAssessmentId!, educatorUserId),
    enabled: !!sectionId && !!selectedAssessmentId,
  });

  const updateClassMutation = useMutation({
    mutationFn: (payload: Parameters<typeof portalApi.updateFacultyClass>[1]) =>
      portalApi.updateFacultyClass(sectionId!, payload),
    onSuccess: () => {
      setEditDialogOpen(false);
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-detail', sectionId, educatorUserId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'current-classes', educatorUserId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'past-classes', educatorUserId] });
    },
    onError: (mutationErr) => {
      setMutationError(mutationErr instanceof Error ? mutationErr.message : 'Unable to update class');
    },
  });

  const addAssessmentMutation = useMutation({
    mutationFn: (payload: AddAssessmentPayload) =>
      portalApi.addFacultyClassAssessment(sectionId!, payload, educatorUserId),
    onSuccess: (_, variables) => {
      setAssessmentDialogOpen(false);
      setMutationError(null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('assessment_id', variables.assessment_id);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-detail', sectionId, educatorUserId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-students', sectionId, educatorUserId] });
    },
    onError: (mutationErr) => {
      setMutationError(mutationErr instanceof Error ? mutationErr.message : 'Unable to add assessment');
    },
  });

  const updateAssessmentDatesMutation = useMutation({
    mutationFn: (payload: AssessmentDatesPayload & { assessment_id: string }) =>
      portalApi.addFacultyClassAssessment(sectionId!, payload, educatorUserId),
    onSuccess: () => {
      setAssessmentDatesDialogOpen(false);
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-detail', sectionId, educatorUserId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-students', sectionId, educatorUserId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'faculty', 'class-question-analysis', sectionId, educatorUserId] });
    },
    onError: (mutationErr) => {
      setMutationError(mutationErr instanceof Error ? mutationErr.message : 'Unable to update assessment dates');
    },
  });

  const readinessLabel: Record<FacultyReadinessLevel, string> = {
    ready: 'Ready',
    ready_with_review: 'Ready with Review',
    not_ready: 'Not Ready',
    pending: 'Pending',
  };

  const selectedAssessment = useMemo(
    () => detail?.assessments.find((item) => item.assessment_id === selectedAssessmentId) || null,
    [detail?.assessments, selectedAssessmentId]
  );

  const selectedAssessmentProgress = useMemo(() => {
    if (!selectedAssessment) {
      return {
        notStarted: 0,
        inProgress: 0,
        completed: 0,
      };
    }

    const notStarted = Math.max(
      selectedAssessment.enrolled_students_count - selectedAssessment.participating_student_count,
      0
    );
    const inProgress = Math.max(
      selectedAssessment.participating_student_count - selectedAssessment.completed_students_count,
      0
    );

    return {
      notStarted,
      inProgress,
      completed: selectedAssessment.completed_students_count,
    };
  }, [selectedAssessment]);

  const availableAssessmentOptions = useMemo(() => {
    const assignedIds = new Set(detail?.assessments.map((item) => item.assessment_id) || []);
    return assessmentOptions.filter((option) => option.is_active && !assignedIds.has(option.assessment_id));
  }, [assessmentOptions, detail?.assessments]);

  const displayedStudents = useMemo(() => {
    const studentDisplayName = (student: FacultyClassStudent) =>
      `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email || '';

    const sorted = [...students].sort((left, right) => {
      if (sortMode === 'status') {
        const statusRank: Record<FacultyClassStudent['assessment_status'], number> = {
          incomplete: 0,
          complete: 1,
        };
        const rankDifference = statusRank[left.assessment_status] - statusRank[right.assessment_status];
        if (rankDifference !== 0) return rankDifference;
      }

      if (sortMode === 'readiness') {
        const readinessRank: Record<FacultyReadinessLevel, number> = {
          pending: 0,
          not_ready: 1,
          ready_with_review: 2,
          ready: 3,
        };
        const rankDifference = readinessRank[left.readiness_level] - readinessRank[right.readiness_level];
        if (rankDifference !== 0) return rankDifference;
      }

      const leftName = studentDisplayName(left).toLowerCase();
      const rightName = studentDisplayName(right).toLowerCase();
      return leftName.localeCompare(rightName);
    });

    if (isSortDescending) {
      sorted.reverse();
    }

    return sorted;
  }, [students, sortMode, isSortDescending]);

  const enrolledStudentsByLastName = useMemo(() => {
    const toSortableName = (value?: string | null) => (value || '').trim().toLowerCase();

    return [...students].sort((left, right) => {
      const lastNameDifference = toSortableName(left.last_name).localeCompare(toSortableName(right.last_name));
      if (lastNameDifference !== 0) return lastNameDifference;

      const firstNameDifference = toSortableName(left.first_name).localeCompare(toSortableName(right.first_name));
      if (firstNameDifference !== 0) return firstNameDifference;

      return (left.email || '').toLowerCase().localeCompare((right.email || '').toLowerCase());
    });
  }, [students]);

  const completedStudents = useMemo(
    () => students.filter((student) => student.assessment_status === 'complete'),
    [students]
  );

  const skillSnapshot = useMemo(() => {
    const denominator = completedStudents.length;

    const countSkills = (selector: (student: FacultyClassStudent) => string[]) => {
      const counts = new Map<string, number>();

      completedStudents.forEach((student) => {
        const uniqueSkills = new Set(selector(student).filter((skill) => skill && skill.trim().length > 0));
        uniqueSkills.forEach((skill) => {
          counts.set(skill, (counts.get(skill) || 0) + 1);
        });
      });

      return Array.from(counts.entries())
        .map(([skill, count]) => ({
          skill,
          count,
          percentage: denominator > 0 ? (count / denominator) * 100 : 0,
        }))
        .sort((left, right) => right.count - left.count || left.skill.localeCompare(right.skill));
    };

    const flaggedAll = countSkills((student) => student.review_skill_names || []);
    const masteredAll = countSkills((student) => student.mastered_skill_names || []);

    return {
      denominator,
      flaggedAll,
      masteredAll,
      flaggedTop: flaggedAll.slice(0, 5),
      masteredTop: masteredAll.slice(0, 5),
    };
  }, [completedStudents]);

  const displayedQuestionAnalysis = useMemo(() => {
    const sorted = [...questionAnalysis].sort((left, right) => {
      if (questionSortMode === 'correct_rate') {
        const rateDifference = left.correct_rate - right.correct_rate;
        if (rateDifference !== 0) return rateDifference;
      }

      if (questionSortMode === 'average_time') {
        const leftTime = left.average_time_seconds ?? Number.POSITIVE_INFINITY;
        const rightTime = right.average_time_seconds ?? Number.POSITIVE_INFINITY;
        const timeDifference = leftTime - rightTime;
        if (timeDifference !== 0) return timeDifference;
      }

      return left.question_number - right.question_number;
    });

    if (isQuestionSortDescending) {
      sorted.reverse();
    }

    return sorted;
  }, [questionAnalysis, questionSortMode, isQuestionSortDescending]);

  const formatSkillPercentage = (value: number) => `${Math.round(value)}%`;

  const openEditDialog = () => {
    setMutationError(null);
    setEditDialogOpen(true);
  };

  const handleUpdateClass = (payload: EditClassPayload) => {
    if (!detail) return;
    setMutationError(null);
    updateClassMutation.mutate({
      ...payload,
      institution: detail.institution || undefined,
    });
  };

  const handleSelectAssessment = (assessment: FacultyClassAssessment) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('assessment_id', assessment.assessment_id);
      return next;
    });
  };

  const handleBackToAssessmentList = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('assessment_id');
      return next;
    });
  };

  const openAssessmentDatesDialog = () => {
    if (!selectedAssessment) return;
    setMutationError(null);
    setAssessmentDatesDialogOpen(true);
  };

  const handleUpdateAssessmentDates = (payload: AssessmentDatesPayload) => {
    if (!selectedAssessment) return;
    setMutationError(null);
    updateAssessmentDatesMutation.mutate({
      assessment_id: selectedAssessment.assessment_id,
      ...payload,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error ? 'Failed to load class' : 'Class not found'}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(`/educator-portal${educatorOnlyQuerySuffix}`)}>
          Back to Educator Portal
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography variant="h4" fontWeight={700}>{classTitle(detail)}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={openEditDialog}>
              Edit Class
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/educator-portal${educatorOnlyQuerySuffix}`)}>
              Back to Educator Portal
            </Button>
          </Stack>
        </Stack>

        {(mutationError || studentsError || questionAnalysisError) && (
          <Alert severity="error">
            {mutationError || (studentsError ? 'Failed to load assessment roster' : 'Failed to load question analysis')}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography color="text.secondary">Course: {detail.class_name || 'N/A'}</Typography>
            <Typography color="text.secondary">Code: {detail.course_code} {detail.section_code}</Typography>
            <Typography color="text.secondary">Term: {detail.term}</Typography>
            <Typography color="text.secondary">{formatCourseDateRange(detail.start_date, detail.end_date)}</Typography>
            <Typography color="text.secondary">Students Enrolled: {detail.enrolled_count}</Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Assessments</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setMutationError(null);
                  setAssessmentDialogOpen(true);
                }}
                disabled={availableAssessmentOptions.length === 0}
              >
                Add Assessment
              </Button>
            </Stack>

            {detail.assessments.length === 0 ? (
              <Typography color="text.secondary">No assessments linked to this class yet.</Typography>
            ) : (
              <Stack spacing={2}>
                {detail.assessments.map((assessment) => (
                  <Card
                    key={assessment.assessment_id}
                    variant="outlined"
                    sx={{
                      borderColor: assessment.assessment_id === selectedAssessmentId ? 'primary.main' : undefined,
                      borderWidth: assessment.assessment_id === selectedAssessmentId ? 2 : 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => handleSelectAssessment(assessment)}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {assessment.name}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              {formatScheduleRange(assessment.opens_at, assessment.closes_at, assessment.schedule_timezone)}
                            </Typography>
                            <Chip
                              label={availabilityLabel(assessment.availability_status)}
                              color={availabilityColor(assessment.availability_status)}
                              size="small"
                            />
                          </Stack>
                        </Stack>

                        {assessment.availability_status !== 'coming_soon' && (
                          <AssessmentEvidenceSummary
                            completed={assessment.completed_students_count}
                            enrolled={assessment.enrolled_students_count}
                            participating={assessment.participating_student_count}
                            ready={assessment.ready_count}
                            readyWithReview={assessment.ready_with_review_count}
                            notReady={assessment.not_ready_count}
                            incomplete={assessment.incomplete_student_count}
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        {selectedAssessment ? (
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
              <Typography variant="h6" fontWeight={700}>{selectedAssessment.name}</Typography>
              <Button
                variant="outlined"
                onClick={handleBackToAssessmentList}
                aria-label="Close selected assessment details"
              >
                Close
              </Button>
            </Stack>

            <Card>
              <CardContent>
                <Stack spacing={0.75}>
                  <Typography variant="body2">Status: {availabilityLabel(selectedAssessment.availability_status)}</Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {formatScheduleRange(selectedAssessment.opens_at, selectedAssessment.closes_at, selectedAssessment.schedule_timezone)}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Edit assessment active dates"
                      onClick={openAssessmentDatesDialog}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <Stack spacing={0.75} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Not Started: {selectedAssessmentProgress.notStarted} of {selectedAssessment.enrolled_students_count} students
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Progress: {selectedAssessmentProgress.inProgress} of {selectedAssessment.enrolled_students_count} students
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed: {selectedAssessmentProgress.completed} of {selectedAssessment.enrolled_students_count} students
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Skill Snapshot</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Based on completed student assessments ({skillSnapshot.denominator})
                </Typography>

                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Top 5 Skills Needing Review
                      </Typography>
                      <Tooltip
                        title="Percentage of completed student assessments where this skill was identified as needing review."
                        arrow
                      >
                        <IconButton
                          size="small"
                          aria-label="Explain skills needing review percentage"
                          sx={{ p: 0.25 }}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {skillSnapshot.flaggedTop.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        No flagged skills available yet.
                      </Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {skillSnapshot.flaggedTop.map((entry) => (
                          <Stack
                            key={`flagged-${entry.skill}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography variant="body2">{entry.skill}</Typography>
                            <SkillPercentageProgress value={entry.percentage} />
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => setFlaggedSkillsDialogOpen(true)}
                      disabled={skillSnapshot.flaggedAll.length === 0}
                    >
                      See All Skills Needing Review
                    </Button>
                  </Box>

                  <Divider />

                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Top 5 Skills Demonstrated
                      </Typography>
                      <Tooltip
                        title="Percentage of completed student assessments where this skill was demonstrated successfully."
                        arrow
                      >
                        <IconButton
                          size="small"
                          aria-label="Explain skills demonstrated percentage"
                          sx={{ p: 0.25 }}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {skillSnapshot.masteredTop.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        No consistently demonstrated skills available yet.
                      </Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {skillSnapshot.masteredTop.map((entry) => (
                          <Stack
                            key={`mastered-${entry.skill}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography variant="body2">{entry.skill}</Typography>
                            <SkillPercentageProgress value={entry.percentage} />
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => setMasteredSkillsDialogOpen(true)}
                      disabled={skillSnapshot.masteredAll.length === 0}
                    >
                      See All Demonstrated Skills
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Question Analysis</Typography>
                  <Typography color="text.secondary">
                    Based on completed student assessments ({selectedAssessment.completed_students_count})
                  </Typography>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel id="question-sort-mode-label">Sort by</InputLabel>
                      <Select
                        labelId="question-sort-mode-label"
                        value={questionSortMode}
                        label="Sort by"
                        onChange={(event) =>
                          setQuestionSortMode(event.target.value as 'question' | 'correct_rate' | 'average_time')
                        }
                      >
                        <MenuItem value="question">Question Number</MenuItem>
                        <MenuItem value="correct_rate">Correct Rate</MenuItem>
                        <MenuItem value="average_time">Average Time Spent</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton
                      aria-label={isQuestionSortDescending ? 'Set ascending order' : 'Set descending order'}
                      onClick={() => setIsQuestionSortDescending((current) => !current)}
                      size="small"
                      sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                    >
                      {isQuestionSortDescending ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                    </IconButton>
                  </Stack>

                  {questionAnalysisLoading ? (
                    <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : displayedQuestionAnalysis.length === 0 ? (
                    <Typography color="text.secondary">No question analysis available yet.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Question</TableCell>
                          <TableCell>Correct Rate</TableCell>
                          <TableCell>Average Time Spent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedQuestionAnalysis.map((item) => (
                          <TableRow key={item.question_id}>
                            <TableCell>
                              <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
                                {`Question ${item.question_number}:`}
                              </Typography>
                              <MathMarkdown sx={{ '& p': { m: 0 } }}>
                                {item.question_text || ''}
                              </MathMarkdown>
                            </TableCell>
                            <TableCell>{formatSkillPercentage(item.correct_rate)}</TableCell>
                            <TableCell>{formatDuration(item.average_time_seconds)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Students ({displayedStudents.length})</Typography>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="sort-mode-label">Sort by</InputLabel>
                      <Select
                        labelId="sort-mode-label"
                        value={sortMode}
                        label="Sort by"
                        onChange={(event) => setSortMode(event.target.value as 'az' | 'status' | 'readiness')}
                      >
                        <MenuItem value="az">A-Z</MenuItem>
                        <MenuItem value="status">Status</MenuItem>
                        <MenuItem value="readiness">Readiness</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton
                      aria-label={isSortDescending ? 'Set ascending order' : 'Set descending order'}
                      onClick={() => setIsSortDescending((current) => !current)}
                      size="small"
                      sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                    >
                      {isSortDescending ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                    </IconButton>
                  </Stack>

                  {studentsLoading ? (
                    <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : displayedStudents.length === 0 ? (
                    <Typography color="text.secondary">No students available.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Assessment status</TableCell>
                          <TableCell>Readiness level</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedStudents.map((student) => (
                          <TableRow
                            key={student.user_id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/educator-portal/classes/${sectionId}/students/${student.user_id}${portalQuerySuffix}`)}
                          >
                            <TableCell>{`${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email || 'Unnamed student'}</TableCell>
                            <TableCell>{student.assessment_status === 'complete' ? 'Complete' : 'Incomplete'}</TableCell>
                            <TableCell>{readinessLabel[student.readiness_level]}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info">Select an assessment to view student results.</Alert>
            <Card>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6">Students Enrolled ({enrolledStudentsByLastName.length})</Typography>
                  {studentsLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : enrolledStudentsByLastName.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No students enrolled yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {enrolledStudentsByLastName.map((student) => (
                          <TableRow key={`enrolled-${student.user_id}`}>
                            <TableCell>
                              {`${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email || 'Unnamed student'}
                            </TableCell>
                            <TableCell>{student.email || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Stack>

      <SkillBreakdownDialog
        open={flaggedSkillsDialogOpen}
        onClose={() => setFlaggedSkillsDialogOpen(false)}
        title="All Skills Needing Review"
        closeLabel="Close skills needing review dialog"
        description="These skills were marked for review when students showed below 67% mastery of the skill on this assessment. Percentages show how often each skill was marked for review across completed student assessments."
        emptyMessage="No flagged skills available yet."
        entries={skillSnapshot.flaggedAll}
      />

      <SkillBreakdownDialog
        open={masteredSkillsDialogOpen}
        onClose={() => setMasteredSkillsDialogOpen(false)}
        title="All Demonstrated Skills"
        closeLabel="Close demonstrated skills dialog"
        description="These skills were marked as demonstrated when students showed 67% or higher mastery of the skill on this assessment. Percentages show how often each skill was marked as demonstrated across completed student assessments."
        emptyMessage="No demonstrated skills available yet."
        entries={skillSnapshot.masteredAll}
      />

      <EditAssessmentDatesDialog
        open={assessmentDatesDialogOpen}
        onClose={() => setAssessmentDatesDialogOpen(false)}
        onSubmit={handleUpdateAssessmentDates}
        isSubmitting={updateAssessmentDatesMutation.isPending}
        initialOpensAt={selectedAssessment?.opens_at}
        initialClosesAt={selectedAssessment?.closes_at}
        initialTimeZone={selectedAssessment?.schedule_timezone}
      />

      <EditClassDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateClass}
        isSubmitting={updateClassMutation.isPending}
        classDetail={detail}
      />

      <AddAssessmentDialog
        open={assessmentDialogOpen}
        onClose={() => setAssessmentDialogOpen(false)}
        onSubmit={(payload) => {
          setMutationError(null);
          addAssessmentMutation.mutate(payload);
        }}
        isSubmitting={addAssessmentMutation.isPending}
        assessmentOptions={availableAssessmentOptions}
      />
    </Box>
  );
}
